import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { CheckCircle, XCircle, AlertTriangle, Eye, Clock, CheckCircle2, X, Download, FileText, Filter } from 'lucide-react';
import { getAuthToken } from '../../services/api';
import './astyles/AdminAssignments.css';
import { exportToCSV, exportToPDF, formatDataForExport } from '../../utils/exportUtils';

// This interface must match the one in our assignmentModel.ts
interface PendingAssignment {
  id: number; // This is the ID of the task_applications record
  task_id: number;
  volunteer_id: number;
  volunteer_name: string;
  task_title: string;
  project_name: string;
  status: string;
  created_at: string;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://mellow-commitment-production-bf3d.up.railway.app';

const AdminAssignments: React.FC = () => {
  const [assignments, setAssignments] = useState<PendingAssignment[]>([]);
  const [filteredAssignments, setFilteredAssignments] = useState<PendingAssignment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedAssignment, setSelectedAssignment] = useState<PendingAssignment | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [exportLoading, setExportLoading] = useState<boolean>(false);

  // --- Fetch all assignments from the API ---
  const fetchAssignments = async () => {
    try {
      setIsLoading(true);
      const token = getAuthToken();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'user-id': '1' // Admin user ID
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await axios.get<PendingAssignment[]>(`${API_BASE_URL}/api/assignments/pending`, { headers });
      setAssignments(response.data);
      setFilteredAssignments(response.data);
      setError(null);
    } catch (err) {
      console.error("Failed to fetch assignments:", err);
      setError("Could not load task assignments.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAssignments();
  }, []);

  // Filter assignments by status
  useEffect(() => {
    if (statusFilter === 'all') {
      setFilteredAssignments(assignments);
    } else {
      const filtered = assignments.filter(assignment => assignment.status === statusFilter);
      setFilteredAssignments(filtered);
    }
  }, [assignments, statusFilter]);

  // --- Handle Status Updates ---
  const handleUpdateStatus = async (assignmentId: number, status: 'approved' | 'rejected' | 'ongoing' | 'completed') => {
    const originalAssignments = [...assignments];
    try {
      // Make the API call to update the status
      const token = getAuthToken();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'user-id': '1' // Admin user ID
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      await axios.put(`${API_BASE_URL}/api/assignments/${assignmentId}/status`, { status }, { headers });

      // Update the local state
      setAssignments(prev => 
        prev.map(assignment => 
          assignment.id === assignmentId 
            ? { ...assignment, status }
            : assignment
        )
      );
      
      // Update filtered assignments as well
      setFilteredAssignments(prev => 
        prev.map(assignment => 
          assignment.id === assignmentId 
            ? { ...assignment, status }
            : assignment
        )
      );

      // Close modal if it's open
      setShowModal(false);
      setSelectedAssignment(null);

    } catch (err) {
      console.error(`Failed to update assignment status:`, err);
      alert(`Error: Could not update assignment status.`);
    }
  };

  // --- Handle View Assignment ---
  const handleViewAssignment = (assignment: PendingAssignment) => {
    setSelectedAssignment(assignment);
    setShowModal(true);
  };

  // --- Close Modal ---
  const closeModal = () => {
    setShowModal(false);
    setSelectedAssignment(null);
  };

  // Helper to format the date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-CA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Export functions
  const handleExportCSV = () => {
    const formattedData = formatDataForExport(filteredAssignments, 'assignments');
    exportToCSV(formattedData, `task_assignments_${new Date().toISOString().split('T')[0]}`);
  };

  const handleExportPDF = async () => {
    setExportLoading(true);
    try {
      const formattedData = formatDataForExport(filteredAssignments, 'assignments');
      await exportToPDF(formattedData, `task_assignments_${new Date().toISOString().split('T')[0]}`, 'Task Assignments');
    } finally {
      setExportLoading(false);
    }
  };

  // Get unique statuses for filter dropdown
  const getUniqueStatuses = () => {
    const statuses = assignments.map(a => a.status);
    return Array.from(new Set(statuses)).sort();
  };

  return (
    <div className="admin-page-container">
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Task Assignments</h1>
          <p className="admin-page-subtitle">Approve volunteers who have signed up for specific tasks.</p>
        </div>
        
        <div className="admin-controls">
          <div className="filter-container">
            <Filter size={16} />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="status-filter"
            >
              <option value="all">All Statuses</option>
              {getUniqueStatuses().map(status => (
                <option key={status} value={status}>
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </option>
              ))}
            </select>
          </div>

          <div className="export-buttons">
            <button
              onClick={handleExportCSV}
              className="export-btn csv-btn"
              disabled={filteredAssignments.length === 0}
            >
              <Download size={16} />
              Export CSV
            </button>
            <button
              onClick={handleExportPDF}
              className="export-btn pdf-btn"
              disabled={filteredAssignments.length === 0 || exportLoading}
            >
              <FileText size={16} />
              {exportLoading ? 'Exporting...' : 'Export PDF'}
            </button>
          </div>
        </div>
      </div>

      <div className="admin-table-container">
        {isLoading && <p style={{ textAlign: 'center', padding: '2rem' }}>Loading assignments...</p>}
        
        {error && (
          <div className="error-message-box">
            <AlertTriangle size={24} />
            <div>
              <strong>Loading Error</strong>
              <p>{error}</p>
            </div>
          </div>
        )}

        {!isLoading && filteredAssignments.length === 0 && !error && (
          <div className="no-data-message">
            <h3>All Caught Up!</h3>
            <p>There are no task assignments to review{statusFilter !== 'all' ? ` with status "${statusFilter}"` : ''}.</p>
          </div>
        )}

        {!isLoading && filteredAssignments.length > 0 && !error && (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Volunteer Name</th>
                <th>Task Title</th>
                <th>Project Name</th>
                <th>Status</th>
                <th>Applied Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAssignments.map(item => (
                <tr key={item.id}>
                  <td>{item.volunteer_name}</td>
                  <td>{item.task_title}</td>
                  <td>{item.project_name}</td>
                  <td>
                    <span className={`status-badge status-${item.status}`}>
                      {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                    </span>
                  </td>
                  <td>{formatDate(item.created_at)}</td>
                  <td className="actions-cell">
                    <button 
                      onClick={() => handleViewAssignment(item)} 
                      className="action-btn view-btn"
                    >
                      <Eye size={16} />
                      <span>View</span>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Modal for Assignment Actions */}
        {showModal && selectedAssignment && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Task Assignment Details</h3>
                <button onClick={closeModal} className="close-btn">
                  <X size={20} />
                </button>
              </div>
              
              <div className="modal-body">
                <div className="assignment-details">
                  <p><strong>Volunteer:</strong> {selectedAssignment.volunteer_name}</p>
                  <p><strong>Task:</strong> {selectedAssignment.task_title}</p>
                  <p><strong>Project:</strong> {selectedAssignment.project_name}</p>
                  <p><strong>Current Status:</strong> 
                    <span className={`status-badge status-${selectedAssignment.status}`}>
                      {selectedAssignment.status.charAt(0).toUpperCase() + selectedAssignment.status.slice(1)}
                    </span>
                  </p>
                  <p><strong>Applied Date:</strong> {formatDate(selectedAssignment.created_at)}</p>
                </div>

                <div className="modal-actions">
                  {selectedAssignment.status === 'applied' && (
                    <>
                      <button 
                        onClick={() => handleUpdateStatus(selectedAssignment.id, 'approved')} 
                        className="action-btn approve-btn"
                      >
                        <CheckCircle size={16} />
                        <span>Approve</span>
                      </button>
                      <button 
                        onClick={() => handleUpdateStatus(selectedAssignment.id, 'rejected')} 
                        className="action-btn reject-btn"
                      >
                        <XCircle size={16} />
                        <span>Reject</span>
                      </button>
                    </>
                  )}
                  
                  {selectedAssignment.status === 'approved' && (
                    <>
                      <button 
                        onClick={() => handleUpdateStatus(selectedAssignment.id, 'ongoing')} 
                        className="action-btn ongoing-btn"
                      >
                        <Clock size={16} />
                        <span>Mark as Ongoing</span>
                      </button>
                      <button 
                        onClick={() => handleUpdateStatus(selectedAssignment.id, 'completed')} 
                        className="action-btn complete-btn"
                      >
                        <CheckCircle2 size={16} />
                        <span>Mark as Completed</span>
                      </button>
                    </>
                  )}

                  {selectedAssignment.status === 'ongoing' && (
                    <>
                      <button 
                        onClick={() => handleUpdateStatus(selectedAssignment.id, 'completed')} 
                        className="action-btn complete-btn"
                      >
                        <CheckCircle2 size={16} />
                        <span>Mark as Completed</span>
                      </button>
                      <button 
                        onClick={() => handleUpdateStatus(selectedAssignment.id, 'rejected')} 
                        className="action-btn withdraw-btn"
                      >
                        <X size={16} />
                        <span>Withdraw</span>
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAssignments;